import React, { useState } from 'react';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import Modal from './Modal';
import Button from './Button';
import { Input } from './Input';

const ICON_CLASSES = {
  danger: 'bg-red-100 text-red-600',
  default: 'bg-themePrimary/10 text-themePrimary',
};

// Replaces every window.confirm/window.prompt in the app with one styled
// dialog. Set requireInput to collect a value (e.g. a verification key)
// instead of a plain yes/no — onConfirm receives that value as its argument.
const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  tone = 'light',
  requireInput = false,
  inputLabel = 'Verification key',
  inputType = 'password',
  inputPlaceholder,
}) => {
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (submitting) return;
    setValue('');
    onClose?.();
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm?.(requireInput ? value : undefined);
      setValue('');
    } finally {
      setSubmitting(false);
    }
  };

  const Icon = variant === 'danger' ? AlertTriangle : HelpCircle;
  const disabled = submitting || (requireInput && !value.trim());

  return (
    <Modal
      open={open}
      onClose={handleClose}
      tone={tone}
      size="sm"
      footer={
        <>
          <Button variant="outline" tone={tone} onClick={handleClose} disabled={submitting}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            tone={tone}
            onClick={handleConfirm}
            loading={submitting}
            disabled={disabled}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex gap-4">
        <div className={['w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0', ICON_CLASSES[variant]].join(' ')}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          {title && <p className={['font-semibold', tone === 'dark' ? 'text-white' : 'text-slate-900'].join(' ')}>{title}</p>}
          {description && (
            <p className={['text-sm mt-1', tone === 'dark' ? 'text-slate-400' : 'text-slate-500'].join(' ')}>{description}</p>
          )}
          {requireInput && (
            <div className="mt-3">
              <Input
                type={inputType}
                tone={tone}
                label={inputLabel}
                placeholder={inputPlaceholder}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                autoFocus
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmDialog;
